import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import GlobalHeader from "../../components/ui/GlobalHeader";
import SidebarNavigation from "../../components/ui/SidebarNavigation";
import BreadcrumbTrail from "../../components/ui/BreadcrumbTrail";
import QuickActionToolbar from "../../components/ui/QuickActionToolbar";
import DocumentEditor from "./components/DocumentEditor";
import DocumentOutline from "./components/DocumentOutline";
import VersionControl from "./components/VersionControl";
import PublicationWorkflow from "./components/PublicationWorkflow";

import Button from "../../components/ui/Button";
import MainNavigation from "components/ui/MainNavigation";
import NavigationHeader from "components/ui/NavigationHeader";
import { useTranslation } from "react-i18next";
import BreadcrumbNavigation from "components/ui/BreadcrumbNavigation";

const PrivacyPolicyManagement = () => {
  const { t } = useTranslation();

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [sidebarExpanded, setSidebarExpanded] = useState(false);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [selectedSection, setSelectedSection] = useState("1");
  const [currentVersion, setCurrentVersion] = useState(null);
  const [documentContent, setDocumentContent] = useState("");

  // Mock data for document sections
  const [documentSections, setDocumentSections] = useState([
    {
      id: "1",
      title: "Introduction",
      level: 1,
      subsections: [
        {
          id: "1.1",
          title: "Purpose of This Policy",
          level: 2,
          subsections: [],
        },
        {
          id: "1.2",
          title: "Scope and Application",
          level: 2,
          subsections: [],
        },
      ],
    },
    {
      id: "2",
      title: "Information We Collect",
      level: 1,
      subsections: [
        { id: "2.1", title: "Personal Information", level: 2, subsections: [] },
        { id: "2.2", title: "Usage Data", level: 2, subsections: [] },
        { id: "2.3", title: "Device Information", level: 2, subsections: [] },
      ],
    },
    {
      id: "3",
      title: "How We Use Your Information",
      level: 1,
      subsections: [
        { id: "3.1", title: "Service Provision", level: 2, subsections: [] },
        { id: "3.2", title: "Communication", level: 2, subsections: [] },
        { id: "3.3", title: "Legal Compliance", level: 2, subsections: [] },
      ],
    },
    {
      id: "4",
      title: "Information Sharing",
      level: 1,
      subsections: [
        { id: "4.1", title: "Third-Party Services", level: 2, subsections: [] },
        { id: "4.2", title: "Legal Requirements", level: 2, subsections: [] },
      ],
    },
    {
      id: "5",
      title: "Your Rights and Choices",
      level: 1,
      subsections: [
        {
          id: "5.1",
          title: "Access and Correction",
          level: 2,
          subsections: [],
        },
        { id: "5.2", title: "Data Portability", level: 2, subsections: [] },
        { id: "5.3", title: "Deletion Rights", level: 2, subsections: [] },
      ],
    },
    {
      id: "6",
      title: "Data Security",
      level: 1,
      subsections: [],
    },
    {
      id: "7",
      title: "Contact Information",
      level: 1,
      subsections: [],
    },
  ]);
  const user = {
    name: t("userName"),
    email: t("userEmail"),
    avatar:
      "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150",
  };

  // Mock data for versions
  const [versions, setVersions] = useState([
    {
      id: "1",
      title: "Initial Privacy Policy",
      description:
        "First version of the privacy policy covering basic data collection and usage.",
      type: "major",
      status: "published",
      createdAt: new Date("2024-01-15"),
      createdBy: "Sarah Johnson",
      changes: [
        { type: "addition", count: 15 },
        { type: "deletion", count: 0 },
        { type: "modification", count: 0 },
      ],
    },
    {
      id: "2",
      title: "GDPR Compliance Update",
      description:
        "Updated policy to include GDPR compliance requirements and user rights.",
      type: "major",
      status: "published",
      createdAt: new Date("2024-03-20"),
      createdBy: "Michael Chen",
      changes: [
        { type: "addition", count: 8 },
        { type: "deletion", count: 2 },
        { type: "modification", count: 12 },
      ],
    },
    {
      id: "3",
      title: "Cookie Policy Integration",
      description:
        "Added comprehensive cookie policy section and tracking disclosures.",
      type: "minor",
      status: "approved",
      createdAt: new Date("2024-07-10"),
      createdBy: "Emily Davis",
      changes: [
        { type: "addition", count: 5 },
        { type: "deletion", count: 1 },
        { type: "modification", count: 3 },
      ],
    },
    {
      id: "4",
      title: "Data Retention Updates",
      description:
        "Updated data retention policies and added clearer deletion procedures.",
      type: "minor",
      status: "review",
      createdAt: new Date("2024-08-05"),
      createdBy: "John Doe",
      changes: [
        { type: "addition", count: 3 },
        { type: "deletion", count: 0 },
        { type: "modification", count: 7 },
      ],
    },
  ]);

  // Mock approvers data
  const approvers = [
    {
      id: "1",
      name: "Sarah Johnson",
      role: "Legal Counsel",
      email: "sarah.johnson@company.com",
    },
    {
      id: "2",
      name: "Michael Chen",
      role: "Privacy Officer",
      email: "michael.chen@company.com",
    },
    {
      id: "3",
      name: "Emily Davis",
      role: "Compliance Manager",
      email: "emily.davis@company.com",
    },
    {
      id: "4",
      name: "Robert Wilson",
      role: "Chief Legal Officer",
      email: "robert.wilson@company.com",
    },
  ];

  // Initialize with default content
  useEffect(() => {
    const defaultContent = `<h1>Privacy Policy</h1>
<p><strong>Effective Date:</strong> ${new Date()?.toLocaleDateString()}</p>
<p><strong>Last Updated:</strong> ${new Date()?.toLocaleDateString()}</p>

<h2>1. Introduction</h2>
<p>Welcome to Business Manager Pro. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our business management platform.</p>

<h2>2. Information We Collect</h2>
<p>We collect information you provide directly to us, such as when you create an account, make a purchase, or contact us for support. This may include:</p>
<ul>
<li>Personal identification information (name, email address, phone number)</li>
<li>Business information (company name, industry, size)</li>
<li>Payment information (processed securely through third-party providers)</li>
<li>Usage data and analytics</li>
</ul>

<h2>3. How We Use Your Information</h2>
<p>We use the information we collect to:</p>
<ul>
<li>Provide, maintain, and improve our services</li>
<li>Process transactions and send related information</li>
<li>Send technical notices, updates, and support messages</li>
<li>Respond to your comments and questions</li>
<li>Comply with legal obligations</li>
</ul>

<h2>4. Information Sharing</h2>
<p>We do not sell, trade, or otherwise transfer your personal information to third parties without your consent, except as described in this policy.</p>

<h2>5. Your Rights</h2>
<p>You have the right to access, update, or delete your personal information. You may also opt out of certain communications and request data portability where applicable.</p>

<h2>6. Data Security</h2>
<p>We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction.</p>

<h2>7. Contact Us</h2>
<p>If you have questions about this Privacy Policy, please contact us at:</p>
<p>Email: privacy@businessmanagerpro.com<br>
Phone: (555) 123-4567<br>
Address: 123 Business Ave, Suite 100, City, State 12345</p>`;

    setDocumentContent(defaultContent);
    setCurrentVersion(versions?.[versions?.length - 1]);
  }, []);

  const handleSidebarToggle = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const handleContentChange = (newContent) => {
    setDocumentContent(newContent);
  };

  const handleSectionSelect = (sectionId) => {
    setSelectedSection(sectionId);
  };

  const handleAddSection = (newSection) => {
    setDocumentSections((prev) => [...prev, newSection]);
  };

  const handleDeleteSection = (sectionId) => {
    setDocumentSections((prev) =>
      prev?.filter((section) => section?.id !== sectionId)
    );
  };

  const handleVersionSelect = (version) => {
    setCurrentVersion(version);
  };

  const handleCreateVersion = (versionData) => {
    setVersions((prev) => [...prev, versionData]);
  };

  const handlePublishVersion = (versionId) => {
    setVersions((prev) =>
      prev?.map((version) =>
        version?.id === versionId
          ? { ...version, status: "published" }
          : version
      )
    );
  };

  const handleDeleteVersion = (versionId) => {
    setVersions((prev) => prev?.filter((version) => version?.id !== versionId));
  };

  const handleStatusChange = (newStatus) => {
    if (currentVersion) {
      setCurrentVersion((prev) => ({ ...prev, status: newStatus }));
      setVersions((prev) =>
        prev?.map((version) =>
          version?.id === currentVersion?.id
            ? { ...version, status: newStatus }
            : version
        )
      );
    }
  };

  const handleRequestApproval = (approvalData) => {
    console.log("Approval requested:", approvalData);
    handleStatusChange("review");
  };

  const handleApprove = () => {
    handleStatusChange("approved");
  };

  const handleReject = () => {
    handleStatusChange("draft");
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Privacy Policy Management - Business Manager Pro</title>
        <meta
          name="description"
          content="Create, update, and maintain privacy documentation with version control and change tracking capabilities."
        />
      </Helmet>

      <div className="p-6">
        <div className="max-w-full mx-auto">
          {/* Page Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-foreground mb-2">
                Privacy Policy Management
              </h1>
              <p className="text-muted-foreground">
                Create, update, and maintain privacy documentation with version
                control and change tracking.
              </p>
            </div>

            <div className="flex items-center space-x-3">
              <Button variant="outline" iconName="Download" iconPosition="left">
                Export PDF
              </Button>
              <Button variant="default" iconName="Save" iconPosition="left">
                Save Changes
              </Button>
            </div>
          </div>

          <QuickActionToolbar />

          {/* Main Content Layout */}
          <div className="flex gap-6 h-[calc(100vh-12rem)]">
            {/* Document Outline */}
            <DocumentOutline
              sections={documentSections}
              selectedSection={selectedSection}
              onSectionSelect={handleSectionSelect}
              onAddSection={handleAddSection}
              onDeleteSection={handleDeleteSection}
              onReorderSections={(reorderedSections) =>
                setDocumentSections(reorderedSections)
              }
            />

            {/* Document Editor */}
            <DocumentEditor
              content={documentContent}
              onContentChange={handleContentChange}
              isPreviewMode={isPreviewMode}
              onTogglePreview={() => setIsPreviewMode(!isPreviewMode)}
              selectedSection={selectedSection}
              onSectionSelect={handleSectionSelect}
            />

            {/* Version Control */}
            <VersionControl
              versions={versions}
              currentVersion={currentVersion}
              onVersionSelect={handleVersionSelect}
              onCreateVersion={handleCreateVersion}
              onPublishVersion={handlePublishVersion}
              onDeleteVersion={handleDeleteVersion}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyManagement;
